/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2226-106014&m=dev
 */

import { Accordion } from '@susu/ui/components'
import { type ReactNode, useMemo, useState } from 'react'

import type { Environment } from '../../../App'
import {
  getCustomerServiceUrl,
  getFaqPageUrl,
} from '../../../config/api.config'
import { type LabelFunction, useLabel } from '../../../i18n'
import { ExternalLink } from '../../../utils/ExternalLink'
import { withNodes } from '../../../utils/withNodes'

const RETURN_CONDITIONS = [
  'orderList.faq.returns.conditionUnworn',
  'orderList.faq.returns.conditionTags',
  'orderList.faq.returns.conditionWindow',
  'orderList.faq.returns.conditionCountry',
] as const

/**
 * The bodies are market-specific policy copy — delivery windows, the 30-day
 * return window, CET opening hours — so a translation is not automatically the
 * right value for a market. Crowdin owns the wording; anything that has to
 * differ per market rather than per language belongs in the CMS instead.
 *
 * `title` is handed to `onFaqOpen` and lands on Avo's `faq_open` as
 * `event_label`, which that schema defines as "faq question in native
 * language" — so the translated title is the intended value, not the English
 * one. `id` carries the stable identity React and the open/closed state need.
 */
const getFaqItems = (
  t: LabelFunction,
  urls: Readonly<{
    delivery: string
    returns: string
    overview: string
    customerService: string
  }>,
): Array<{ id: string; title: string; content: ReactNode }> => {
  const faqPageLink = (href: string) => (
    <ExternalLink href={href}>{t('orderList.faq.link.faqPage')}</ExternalLink>
  )
  const customerServiceLink = (
    <ExternalLink href={urls.customerService}>
      {t('orderList.faq.link.customerService')}
    </ExternalLink>
  )

  return [
    {
      id: 'delivery',
      title: t('orderList.faq.delivery.title'),
      content: withNodes(t('orderList.faq.delivery.body'), {
        faqPage: faqPageLink(urls.delivery),
      }),
    },
    {
      id: 'returns',
      title: t('orderList.faq.returns.title'),
      content: (
        <>
          <p>{t('orderList.faq.returns.intro')}</p>
          <p className="mt-8">{t('orderList.faq.returns.conditionsTitle')}</p>
          <ul className="mt-4 list-disc pl-20">
            {RETURN_CONDITIONS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
          <p className="mt-8">
            {withNodes(t('orderList.faq.returns.help'), {
              customerService: customerServiceLink,
              faqPage: faqPageLink(urls.returns),
            })}
          </p>
        </>
      ),
    },
    {
      id: 'needHelp',
      title: t('orderList.faq.needHelp.title'),
      content: withNodes(t('orderList.faq.needHelp.body'), {
        faqPage: faqPageLink(urls.overview),
      }),
    },
  ]
}

interface FaqSupportProps {
  env: Environment
  onFaqOpen?: (title: string) => void
}

export default function FaqSupport({ env, onFaqOpen }: FaqSupportProps) {
  const t = useLabel()
  const [openId, setOpenId] = useState<string | null>(null)
  const faqItems = useMemo(
    () =>
      getFaqItems(t, {
        delivery: getFaqPageUrl(env, '/delivery-time-cost.html'),
        returns: getFaqPageUrl(env, '/returns-refunds.html'),
        overview: getFaqPageUrl(env, '/overview.html'),
        customerService: getCustomerServiceUrl(),
      }),
    [env, t],
  )

  const handleToggle = (id: string, title: string) => {
    const isOpening = openId !== id
    setOpenId(isOpening ? id : null)
    if (isOpening) {
      onFaqOpen?.(title)
    }
  }

  return (
    <div className="flex flex-col gap-16">
      <h2 className="text-xl leading-xl lg:text-2xl lg:leading-2xl font-medium text-text">
        {t('orderList.faq.title')}
      </h2>
      <div className="flex flex-col">
        {faqItems.map((item) => (
          <Accordion
            key={item.id}
            title={item.title}
            size="m"
            open={openId === item.id}
            onClick={() => handleToggle(item.id, item.title)}
          >
            <div className="text-sm leading-sm font-light text-text-secondary">
              {item.content}
            </div>
          </Accordion>
        ))}
      </div>
    </div>
  )
}
